// hooks/faq/useFaq.ts
import { useState, useEffect, useCallback } from "react";
import { Faq, FaqToBackend } from "../../../types/features/Faq";
import { faqService } from "../../../services/faqService";
import useToken from "../auth/useToken";

export default function useFaqs() {
  const { token } = useToken();
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchFaqs = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await faqService.getAll(page, perPage);
      setFaqs(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des FAQ");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchFaqById = useCallback(async (id: string): Promise<Faq | null> => {
    setLoading(true);
    setError(null);
    try {
      const faq = await faqService.getById(id);
      if (!faq) {
        setError("FAQ non trouvée");
      }
      return faq;
    } catch (err) {
      setError("Erreur lors du chargement de la FAQ");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔒 PROTÉGÉ - Token requis
  const createFaq = useCallback(async (data: FaqToBackend): Promise<Faq | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const newFaq = await faqService.create(token, data);
      setFaqs((prev) => [...prev, newFaq].sort((a, b) => a.order - b.order));
      return newFaq;
    } catch (err: any) {
      setError("Erreur lors de la création de la FAQ");
      console.error(err.response);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const updateFaq = useCallback(async (id: string, data: Partial<FaqToBackend>): Promise<Faq | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const updatedFaq = await faqService.update(token, id, data);
      setFaqs((prev) => prev.map((f) => (f.id === id ? updatedFaq : f)).sort((a, b) => a.order - b.order));
      return updatedFaq;
    } catch (err) {
      setError("Erreur lors de la mise à jour de la FAQ");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteFaq = useCallback(async (id: string): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await faqService.delete(token, id);
      setFaqs((prev) => prev.filter((f) => f.id !== id));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression de la FAQ");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteManyFaqs = useCallback(async (ids: string[]): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await faqService.deleteMany(token, ids);
      setFaqs((prev) => prev.filter((f) => !ids.includes(f.id)));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression multiple");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const toggleVisibility = useCallback(async (id: string, currentStatus: boolean): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    try {
      const updated = await faqService.update(token, id, { is_visible: !currentStatus });
      setFaqs((prev) => prev.map((f) => (f.id === id ? updated : f)));
      return true;
    } catch (err) {
      setError("Erreur lors du changement de visibilité");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const reorderFaqs = useCallback(async (ids: string[]): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    try {
      await faqService.reorder(token, ids);
      setFaqs((prev) => {
        const reorderedFaqs: Faq[] = [];
        ids.forEach((id, index) => {
          const faq = prev.find((f) => f.id === id);
          if (faq) {
            reorderedFaqs.push({ ...faq, order: index + 1 });
          }
        });
        return reorderedFaqs;
      });
      return true;
    } catch (err) {
      setError("Erreur lors du réordonnancement");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  return {
    faqs,
    loading,
    error,
    pagination,
    fetchFaqs,
    fetchFaqById,
    createFaq,
    updateFaq,
    deleteFaq,
    deleteManyFaqs,
    toggleVisibility,
    reorderFaqs,
  };
}